import type { InterviewSession, ResponseStatus, InterviewData } from '../types';

/** Display status that includes date-derived states not stored in the DB. */
export type CampaignDisplayStatus = 'setup' | 'scheduled' | 'active' | 'completed' | 'expired';

/**
 * Derive a display status from the DB status and schedule dates.
 * - `completed` / `expired` → returned as-is
 * - `active` + `opens_at` in future → `'scheduled'`
 * - `active` + `closes_at` in past → `'completed'`
 * - otherwise → DB status
 */
export const getCampaignDisplayStatus = (session: InterviewSession): CampaignDisplayStatus => {
  if (session.status === 'completed' || session.status === 'expired') {
    return session.status;
  }

  const now = new Date();

  if (session.status === 'active') {
    if (session.opens_at && new Date(session.opens_at) > now) {
      return 'scheduled';
    }
    if (session.closes_at && new Date(session.closes_at) < now) {
      return 'completed';
    }
  }

  return session.status;
};

/**
 * Return the response's display status directly from the DB column.
 * The `abandoned` and `completed` statuses for stale rows are set by a pg_cron job, no client-side derivation.
 */
export const getResponseDisplayStatus = (response: Pick<InterviewData, 'response_status'>): ResponseStatus => {
  return response.response_status || 'in_progress';
};

/** Prefer recorded guest identity, then the address on an actual saved booking. */
export const getResponseEmail = (response: Pick<InterviewData, 'interviewee_email' | 'calendar_bookings'>): string | undefined => {
  return response.interviewee_email?.trim()
    || response.calendar_bookings?.find(booking => booking.attendeeEmail?.trim())?.attendeeEmail.trim()
    || undefined;
};

/** A respondent with an invite address is identifiable even without a name. */
export const getResponseDisplayName = (response: Pick<InterviewData, 'interviewee_name' | 'interviewee_email' | 'calendar_bookings'>): string => {
  return response.interviewee_name?.trim() || getResponseEmail(response) || 'Anonymous';
};

/** Status badge color classes for campaign display statuses. */
// Campaign badges are SOLID coloured pills (the brand "full colour" option), not
// washes: a wash is too faint to read as a distinct element on a card. They are
// theme-tuned so the fill clears 3:1 against the card in BOTH worlds (a deep
// fill on the light volt-wash card, a bright fill on the dark card) while the
// label keeps AA on the fill. Semantics: grey = neutral/done, green =
// success/active, amber = in-progress, red = problem. Enforced by the badge
// pairs in lib/theme/contrast.ts (theme-contrast.test.ts).
export const sessionStatusColors: Record<CampaignDisplayStatus, string> = {
  setup: 'bg-muted-foreground text-background',
  scheduled: 'bg-warning text-warning-foreground light:bg-warning-soft',
  active: 'bg-success text-success-foreground',
  completed: 'bg-muted-foreground text-background',
  expired: 'bg-danger-soft text-ink light:bg-danger light:text-danger-foreground',
};

/** Human-readable labels for campaign display statuses. */
export const sessionStatusLabels: Record<CampaignDisplayStatus, string> = {
  setup: 'Setting Up',
  scheduled: 'Scheduled',
  active: 'Active',
  completed: 'Closed',
  expired: 'Expired',
};

/** Response statuses use quiet, theme-aware washes with readable labels. */
export const responseStatusConfig: Record<ResponseStatus, { label: string; className: string }> = {
  pending: {
    label: 'Pending',
    className: 'bg-wash-neutral text-muted-foreground',
  },
  completed: {
    label: 'Completed',
    className: 'bg-wash-success text-foreground',
  },
  in_progress: {
    label: 'In Progress',
    className: 'bg-wash-warning text-foreground',
  },
  low_quality: {
    label: 'Low Quality',
    className: 'bg-wash-warning text-foreground',
  },
  abandoned: {
    label: 'Abandoned',
    className: 'bg-wash-neutral text-muted-foreground',
  },
  insufficient: {
    label: 'No Credits',
    className: 'bg-wash-danger text-foreground',
  },
};

/**
 * Format a date string for display.
 * @param dateString - ISO date string
 * @param style - 'short' uses abbreviated month, 'long' uses full month name
 */
export const formatDate = (dateString: string, style: 'short' | 'long' = 'short'): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: style === 'short' ? 'short' : 'long',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Convert a UTC ISO string to a local datetime-local input value (YYYY-MM-DDTHH:MM). */
export const toLocalDatetimeString = (iso: string): string => {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
